"use client";

import { usePathname } from "next/navigation";

import { useTriggerAction, type BlokAction } from "@/lib/action-link";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";

type HeroReviewCtaProps = {
  label?: string;
  link?: StoryblokLink;
  action?: BlokAction | "";
  onDark: boolean;
};

// A client island so the parent module can stay a server component while the
// button still resolves region-aware links and the booking action.
export default function HeroReviewCta({ label, link, action, onDark }: HeroReviewCtaProps) {
  const region = regionFromPathname(usePathname());
  const resolved = resolveLink(link, region);
  const triggerAction = useTriggerAction();
  const act = action || null;

  if (!label || (!act && resolved.href === "#")) return null;

  // On a dark background a white button reads better than the purple one.
  const className = `mt-2 inline-flex h-14 w-fit items-center justify-center rounded-[8px] px-7 text-body font-bold transition-colors ${
    onDark
      ? "bg-white text-purple-05 hover:bg-purple-01"
      : "bg-purple-03 text-white hover:bg-purple-04"
  }`;

  if (act) {
    return (
      <button
        type="button"
        onClick={(e) => triggerAction(act, e)}
        className={`cursor-pointer ${className}`}
      >
        {label}
      </button>
    );
  }

  return (
    <a
      href={resolved.href}
      {...(resolved.target ? { target: resolved.target } : {})}
      className={className}
    >
      {label}
    </a>
  );
}
